import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MapView from '../MapView';
import MapFilterBar from './MapFilterBar';
import PropertyListSidebar from './PropertyListSidebar';
import { MapSearchViewProps } from '../../types';
import { LatLngExpression } from 'leaflet';

const MapSearchView: React.FC<MapSearchViewProps> = ({
  properties,
  filters,
  onFilterChange,
  onClearFilters,
  loading,
  sortBy,
  onSortChange,
  mapCenter,
  onViewChange,
}) => {
  const { t } = useTranslation();
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleMarkerClick = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    // Scroll to property in sidebar
    if (sidebarRef.current) {
      const propertyElement = sidebarRef.current.querySelector(`[data-property-id="${propertyId}"]`);
      if (propertyElement) {
        propertyElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleMarkerHover = (propertyId: string | null) => {
    setHoveredPropertyId(propertyId);
  };

  const handlePropertySelect = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
  };

  const handlePropertyHover = (propertyId: string | null) => {
    setHoveredPropertyId(propertyId);
  };

  const handleToggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const handleCloseSidebar = () => {
    setShowSidebar(false);
  };

  // Calculate center based on selected property or default
  const effectiveCenter: LatLngExpression = selectedPropertyId
    ? (() => {
        const selected = properties.find(p => p.id === selectedPropertyId);
        return selected ? [selected.latitude, selected.longitude] : mapCenter;
      })()
    : mapCenter;

  return (
    <div className="fixed top-16 inset-x-0 bottom-0 flex bg-gray-100">
      {/* Sidebar - Hidden on mobile by default */}
      {showSidebar && (
        <div
          ref={sidebarRef}
          className="hidden md:flex w-[320px] lg:w-[380px] border-r border-gray-200 bg-white flex-col overflow-hidden"
        >
          <PropertyListSidebar
            properties={properties}
            selectedPropertyId={selectedPropertyId}
            hoveredPropertyId={hoveredPropertyId}
            onPropertySelect={handlePropertySelect}
            onPropertyHover={handlePropertyHover}
            loading={loading}
            sortBy={sortBy}
            onSortChange={onSortChange}
            onClose={handleCloseSidebar}
          />
        </div>
      )}

      {/* Map Container */}
      <div className="flex-1 relative">
        {/* Filter Bar - Overlaid on map */}
        <MapFilterBar
          filters={filters}
          onFilterChange={onFilterChange}
          onClearFilters={onClearFilters}
          propertyCount={properties.length}
          onToggleSidebar={handleToggleSidebar}
          showSidebar={showSidebar}
          onViewChange={onViewChange}
        />

        {/* Map - positioned below filter bar (64px single row) */}
        <div className="absolute top-16 left-0 right-0 bottom-0">
          {properties.length === 0 && !loading ? (
            <div className="h-full flex items-center justify-center bg-gray-50">
              <div className="text-center p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('search.noPropertiesFound')}
                </h3>
                <p className="text-gray-600">
                  {t('search.tryAdjusting')}
                </p>
              </div>
            </div>
          ) : (
            <MapView
              properties={properties}
              center={effectiveCenter}
              zoom={12}
              selectedPropertyId={selectedPropertyId}
              hoveredPropertyId={hoveredPropertyId}
              showPriceMarkers={true}
              onMarkerClick={handleMarkerClick}
              onMarkerHover={handleMarkerHover}
            />
          )}
        </div>
      </div>

      {/* Mobile Bottom Sheet for Property List */}
      {/* TODO: Add mobile bottom sheet implementation */}
    </div>
  );
};

export default MapSearchView;
